import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'

import { Transcoder } from '@img-arena/consoles-api-streaming'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { FormikTextField } from '@/components/inputs/FormikTextField'
import { SelectBox } from '@/components/inputs/SelectBox'
import { ApiError } from '@/service/ApiError'
import { streamingRestApi } from '@/service/streamingApi'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps } from '@/utils/forms'
import { getFormError, onFieldChange } from '@/utils/forms/formik'

import { AWS_REGION_OPTIONS, INPUT_TYPES, INSTANCE_TYPE_OPTIONS } from './TranscoderForm.constants'
import { transcoderFormValidationSchema } from './transcoderForm.validation'

const TranscoderForm = ({ onClose, onSubmit, open = false }: FormDialogProps<Transcoder>) => {
  const { enqueueSnackbar } = useSnackbar()

  const formik = useFormik({
    initialValues: {
      region: '',
      instanceType: '',
      inputType: '',
      instanceCount: 0
    },
    validationSchema: transcoderFormValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!values.region || !values.instanceType || !values.inputType || !values.instanceCount)
        return

      try {
        setSubmitting(true)

        await streamingRestApi.endpoints.transcoders.addTranscoder({
          region: values.region,
          instanceType: values.instanceType,
          inputType: values.inputType,
          instanceCount: values.instanceCount
        })

        onSubmit?.()
        enqueueSnackbar(`Successfully added transcoder: ${values.instanceType}`, {
          variant: 'success'
        })
        setSubmitting(false)
        handleClose()
      } catch (e) {
        const errorMessage = ['Failed to add transcoder', (e as unknown as ApiError)?.message]
          .filter(Boolean)
          .join(': ')
        enqueueSnackbar(errorMessage, { variant: 'error' })
        setSubmitting(false)
        // Throw the error again to be caught by dialog and doesn't close it
        throw errorMessage
      }
    }
  })

  const handleClose = () => {
    formik.resetForm()
    onClose?.()
  }

  return (
    <ConsoleDialog
      open={open}
      title="Add Transcoder"
      onClose={handleClose}
      SaveButtonProps={formikSaveButtonProps(formik)}
    >
      <SelectBox
        label="AWS Region"
        name="region"
        required
        fullWidth
        value={AWS_REGION_OPTIONS.find((option) => option.value === formik.values.region) || null}
        onChange={(value) => onFieldChange(formik, 'region', value.value)}
        getOptionId={(option) => option.value}
        formatListOption={(option) => option.label}
        formatInputOption={(option) => option.label}
        options={AWS_REGION_OPTIONS}
        error={getFormError(formik, 'region')}
        groupBy={(option) => option.region}
      />
      <SelectBox
        label="Instance Type"
        name="instanceType"
        required
        fullWidth
        value={
          INSTANCE_TYPE_OPTIONS.find((option) => option.value === formik.values.instanceType) ||
          null
        }
        getOptionId={(option) => option.value}
        formatListOption={(option) => option.label}
        formatInputOption={(option) => option.label}
        onChange={(value) => onFieldChange(formik, 'instanceType', value.value)}
        options={INSTANCE_TYPE_OPTIONS}
        error={getFormError(formik, 'instanceType')}
      />
      <SelectBox
        label="Server Type"
        name="inputType"
        required
        fullWidth
        value={formik.values.inputType}
        onChange={(value) => onFieldChange(formik, 'inputType', value)}
        options={INPUT_TYPES}
        error={getFormError(formik, 'inputType')}
      />
      <FormikTextField
        type="number"
        label="Number of Transcoders"
        required
        name="instanceCount"
        formik={formik}
      />
    </ConsoleDialog>
  )
}

export default TranscoderForm
